import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { useBooleanVariant } from '@front/common/src/providers/experiment-provider';
import { User } from '@front/common/src/types/commons';
import { getMode, getUniverseRef } from '@front/common/src/ducks/customer/basket-selectors';
import { isWellnessReference } from '@front/common/src/utils/universes';
import { setBasketAttributeValue } from '@front/common/src/ducks/common/basket';

import useCanSubscribeToMembership from './use-can-subscribe-to-membership';

export type UseMembershipOptInParams = {
  user?: User;
};

const useMembershipOptIn = ({ user }: UseMembershipOptInParams) => {
  const dispatch = useDispatch();

  const isLegallyCompliantMembershipOptIn = useBooleanVariant('cst-membership-wellness-legally-compliant');

  const universeReference = useSelector(getUniverseRef);
  const mode = useSelector(getMode);

  const canSubscribeToMembership = useCanSubscribeToMembership({
    user,
    mode,
    universeReference,
  });

  const shouldSelectMembership =
    isWellnessReference(universeReference) && canSubscribeToMembership && !isLegallyCompliantMembershipOptIn;

  const selectMembershipByDefaultIfAvailable = useCallback(() => {
    if (shouldSelectMembership) {
      dispatch(setBasketAttributeValue('isMembershipRequested', true));
    }
  }, [shouldSelectMembership, dispatch]);

  return {
    isLegallyCompliantMembershipOptIn,
    selectMembershipByDefaultIfAvailable,
  };
};

export default useMembershipOptIn;
